import type { Snapshot } from '@ember-data/legacy-compat/-private';
import { EmbeddedRecordsMixin } from '@ember-data/serializer/rest';
import type Store from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import type { SmartInsightsUpsell } from 'lumen/models';
import ApplicationSerializer from 'lumen/serializers/application';

interface UpsellGetResponse {
  'si-upsell': SmartInsightsUpsell[];
}

interface UpsellPutResponse {
  trial_365_requested: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
export default class SmartInsightsUpsellSerializer extends ApplicationSerializer.extend(
  EmbeddedRecordsMixin,
) {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  keyForAttribute(attr: string) {
    // underscore('trial365Requested') → 'trial365_requested' (no leading underscore
    // before the number), but the API uses 'trial_365_requested'. Override explicitly.
    if (attr === 'trial365Requested') {
      return 'trial_365_requested';
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return super.keyForAttribute(attr);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  normalizeResponse(
    store: Store,
    primaryModelClass: ModelSchema,
    payload: UpsellGetResponse | UpsellPutResponse,
    id: string | number,
    requestType: string,
  ) {
    if (requestType === 'queryRecord') {
      const normalizedPayload = {
        'smart-insights/upsell': (payload as UpsellGetResponse)['si-upsell'],
      };

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      return super.normalizeResponse(
        store,
        primaryModelClass,
        normalizedPayload,
        id,
        requestType,
      );
    }

    if (requestType === 'updateRecord') {
      // Wrap the flat PUT response so Ember Data updates the record correctly
      // instead of resetting its attributes.
      const normalizedPayload = {
        'smart-insights/upsell': { id, ...(payload as UpsellPutResponse) },
      };

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      return super.normalizeResponse(
        store,
        primaryModelClass,
        normalizedPayload,
        id,
        requestType,
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return super.normalizeResponse(
      store,
      primaryModelClass,
      payload,
      id,
      requestType,
    );
  }

  serializeIntoHash(
    hash: SmartInsightsUpsell,
    _ModelClass: unknown,
    snapshot: Snapshot,
  ): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
    const rec = snapshot.record as any;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const { trial365Requested } = rec.getProperties('trial365Requested') as {
      trial365Requested: boolean;
    };
    Object.assign(hash, {
      trial_365_requested: trial365Requested,
    });
  }
}
